import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot, Receipt, ShieldCheck, TrendingUp, Sparkles, Camera, Bell, Check,
  FileText, ScanLine, MessageSquare, Send, QrCode, User2, Building2,
} from 'lucide-react';

/**
 * HeroPreview — loop de "vídeo sintético" del producto, 4 escenas
 *  1) Dashboard live (stats + chart + facturas)
 *  2) Crear factura con Verifactu firmando
 *  3) OCR escaneando un ticket
 *  4) Asesor IA respondiendo
 */

const SCENES = [
  { id: 'dashboard', url: 'facturcal.com/dashboard', label: 'Dashboard live' },
  { id: 'invoice',   url: 'facturcal.com/invoices/new', label: 'Crear factura · Verifactu' },
  { id: 'ocr',       url: 'facturcal.com/expenses/inbox', label: 'OCR · Escaneo de ticket' },
  { id: 'ai',        url: 'facturcal.com/ai-advisor', label: 'Asesor IA fiscal' },
];

const fmtEur = (n) => `€${n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ──────────────────────────────────────────────────────────
   ESCENA 1 — Dashboard
   ────────────────────────────────────────────────────────── */
const STATS_CYCLES = [
  { val: 18420, delta: '+24%', pending: 3150, pCount: 5, iva: 2395 },
  { val: 21105, delta: '+38%', pending: 2895, pCount: 4, iva: 2620 },
  { val: 24890, delta: '+52%', pending: 2410, pCount: 3, iva: 2891 },
];
const INVOICES_POOL = [
  { num: 'FAC-00118', client: 'Vélez Diseño SL', amount: 2420.00, status: 'paid' },
  { num: 'FAC-00117', client: 'Estudio Loom',    amount: 890.50,  status: 'sent' },
  { num: 'FAC-00116', client: 'Mercadona SA',    amount: 1205.00, status: 'paid' },
  { num: 'FAC-00115', client: 'Repsol S.A.',     amount: 645.20,  status: 'paid' },
];
const SPARKLINES = [
  'M0 45 L20 38 L40 42 L60 30 L80 32 L100 22 L120 27 L140 18 L160 20 L180 12 L200 15 L220 8 L240 11 L260 6 L280 4',
  'M0 50 L20 44 L40 35 L60 38 L80 28 L100 30 L120 18 L140 22 L160 12 L180 16 L200 8 L220 12 L240 5 L260 8 L280 3',
  'M0 42 L20 40 L40 30 L60 34 L80 24 L100 28 L120 16 L140 20 L160 10 L180 14 L200 5 L220 9 L240 3 L260 6 L280 2',
];

const SceneDashboard = () => {
  const [c, setC] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setC((x) => (x + 1) % STATS_CYCLES.length), 1500);
    return () => clearInterval(id);
  }, []);
  const s = STATS_CYCLES[c];
  return (
    <div className="p-5 space-y-4">
      <div className="grid grid-cols-3 gap-2.5">
        {[
          { val: s.val, lbl: 'Facturado', delta: s.delta, icon: TrendingUp, gradient: 'from-emerald-500 to-teal-400' },
          { val: s.pending, lbl: 'Pendiente', delta: `${s.pCount} facturas`, icon: Receipt, gradient: 'from-amber-500 to-orange-400' },
          { val: s.iva, lbl: 'IVA Q2', delta: 'Modelo 303', icon: ShieldCheck, gradient: 'from-violet-500 to-purple-400' },
        ].map((k, i) => (
          <div key={i} className="bg-gradient-to-br from-stone-50 to-white rounded-xl p-3 border border-stone-100 relative overflow-hidden">
            <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${k.gradient} flex items-center justify-center mb-2 shadow-sm`}>
              <k.icon className="w-3.5 h-3.5 text-white" strokeWidth={2.2} />
            </div>
            <p className="text-[9px] uppercase tracking-wider text-stone-400 font-bold">{k.lbl}</p>
            <AnimatePresence mode="wait">
              <motion.p key={`${i}-${k.val}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35 }}
                className="text-base font-extrabold text-stone-900 leading-tight tabular-nums">
                {fmtEur(k.val).replace(',00', '')}
              </motion.p>
            </AnimatePresence>
            <p className="text-[10px] text-stone-500">{k.delta}</p>
          </div>
        ))}
      </div>

      <div className="bg-stone-50/50 rounded-xl p-4 border border-stone-100">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold text-stone-700">Facturación últimos 30 días</p>
          <motion.span key={c} initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }}
            className="text-[10px] bg-emerald-100 text-emerald-700 font-bold px-2 py-0.5 rounded-full">▲ {[24, 38, 52][c]}%</motion.span>
        </div>
        <svg viewBox="0 0 280 60" className="w-full h-12 overflow-visible">
          <defs>
            <linearGradient id="hpGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#FF6B4A" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#FF6B4A" stopOpacity="0" />
            </linearGradient>
          </defs>
          <motion.path key={`a-${c}`} d={`${SPARKLINES[c]} L280 60 L0 60 Z`} fill="url(#hpGrad)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} />
          <motion.path key={`l-${c}`} d={SPARKLINES[c]} fill="none" stroke="#FF6B4A" strokeWidth="2" strokeLinecap="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.1, ease: 'easeInOut' }} />
          <motion.circle cx="280" cy={[4, 3, 2][c]} r="4" fill="#FF6B4A" animate={{ scale: [1, 1.6, 1], opacity: [1, 0.6, 1] }} transition={{ duration: 1.6, repeat: Infinity }} />
        </svg>
      </div>

      <div className="space-y-1.5">
        {INVOICES_POOL.slice(0, 3).map((inv, i) => (
          <motion.div key={inv.num} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.08 }}
            className="flex items-center gap-3 px-3 py-2 bg-white rounded-lg border border-stone-100">
            <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center"><Receipt className="w-3.5 h-3.5 text-orange-500" /></div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-mono font-bold text-stone-700">{inv.num}</p>
              <p className="text-[10px] text-stone-400 truncate">{inv.client}</p>
            </div>
            <p className="text-xs font-extrabold text-stone-900 tabular-nums">{fmtEur(inv.amount)}</p>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
              {inv.status === 'paid' ? 'Pagada' : 'Enviada'}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────
   ESCENA 2 — Crear factura con Verifactu firmando
   ────────────────────────────────────────────────────────── */
const SceneInvoice = () => {
  // Steps drive what's typed/visible
  // 0: client picked → 1: line added → 2: totals → 3: Verifactu signing → 4: signed
  const [step, setStep] = useState(0);
  useEffect(() => {
    const stops = [400, 1100, 1900, 2800, 3700]; // cumulative ms per step
    const timers = stops.map((ms, idx) => setTimeout(() => setStep(idx), ms));
    // Restart loop every ~5s
    const reset = setTimeout(() => setStep(0), 4800);
    return () => { timers.forEach(clearTimeout); clearTimeout(reset); };
  }, []);
  return (
    <div className="p-5 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-stone-800 flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5 text-orange-500" /> Nueva factura · FAC-00119
        </p>
        <span className="text-[9px] bg-stone-100 text-stone-500 font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Borrador</span>
      </div>

      {/* Client picker */}
      <motion.div initial={{ opacity: 0.4 }} animate={{ opacity: step >= 0 ? 1 : 0.4 }} className="bg-stone-50 rounded-lg p-2.5 border border-stone-100 flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center"><User2 className="w-3.5 h-3.5 text-violet-600" /></div>
        <div className="flex-1">
          <p className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">Cliente</p>
          <p className="text-xs font-extrabold text-stone-800">Estudio Loom · B-12345678</p>
        </div>
        {step >= 0 && <Check className="w-3.5 h-3.5 text-emerald-500" />}
      </motion.div>

      {/* Line items */}
      <div className="bg-white rounded-lg border border-stone-100 overflow-hidden">
        <div className="grid grid-cols-12 px-3 py-1.5 text-[9px] uppercase tracking-wider text-stone-400 font-bold bg-stone-50">
          <span className="col-span-6">Concepto</span>
          <span className="col-span-2 text-right">H/Ud</span>
          <span className="col-span-2 text-right">Precio</span>
          <span className="col-span-2 text-right">Total</span>
        </div>
        <AnimatePresence>
          {step >= 1 && (
            <motion.div key="line1" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
              className="grid grid-cols-12 px-3 py-1.5 text-[11px] text-stone-700 border-b border-stone-50">
              <span className="col-span-6 font-semibold">Consultoría producto</span>
              <span className="col-span-2 text-right tabular-nums">10</span>
              <span className="col-span-2 text-right tabular-nums">€85,00</span>
              <span className="col-span-2 text-right tabular-nums font-bold">€850,00</span>
            </motion.div>
          )}
          {step >= 2 && (
            <motion.div key="line2" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
              className="grid grid-cols-12 px-3 py-1.5 text-[11px] text-stone-700">
              <span className="col-span-6 font-semibold">Diseño UI screens</span>
              <span className="col-span-2 text-right tabular-nums">4</span>
              <span className="col-span-2 text-right tabular-nums">€120,00</span>
              <span className="col-span-2 text-right tabular-nums font-bold">€480,00</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-3 gap-2">
        <motion.div animate={{ opacity: step >= 2 ? 1 : 0.3 }} className="rounded-lg p-2 bg-stone-50 border border-stone-100">
          <p className="text-[9px] uppercase tracking-wider text-stone-400 font-bold">Subtotal</p>
          <p className="text-xs font-extrabold tabular-nums text-stone-800">€1.330,00</p>
        </motion.div>
        <motion.div animate={{ opacity: step >= 2 ? 1 : 0.3 }} className="rounded-lg p-2 bg-stone-50 border border-stone-100">
          <p className="text-[9px] uppercase tracking-wider text-stone-400 font-bold">IVA 21%</p>
          <p className="text-xs font-extrabold tabular-nums text-stone-800">€279,30</p>
        </motion.div>
        <motion.div animate={{ opacity: step >= 2 ? 1 : 0.3 }} className="rounded-lg p-2 bg-gradient-to-br from-orange-500 to-rose-500 text-white">
          <p className="text-[9px] uppercase tracking-wider text-white/80 font-bold">Total</p>
          <p className="text-xs font-extrabold tabular-nums">€1.609,30</p>
        </motion.div>
      </div>

      {/* Verifactu signing → signed */}
      <motion.div
        animate={{
          backgroundColor: step >= 4 ? '#ecfdf5' : step >= 3 ? '#fffbeb' : '#f5f5f4',
          borderColor: step >= 4 ? '#a7f3d0' : step >= 3 ? '#fde68a' : '#e7e5e4',
        }}
        className="rounded-lg p-2.5 border flex items-center gap-2.5"
      >
        <div className="w-8 h-8 rounded-lg bg-white border border-stone-200 flex items-center justify-center shrink-0 relative overflow-hidden">
          <QrCode className="w-4 h-4 text-stone-700" />
          {step === 3 && (
            <motion.div initial={{ y: -16 }} animate={{ y: 16 }} transition={{ duration: 0.6, repeat: 1, ease: 'easeInOut' }} className="absolute inset-x-0 h-0.5 bg-amber-500 shadow-[0_0_8px_2px_rgba(245,158,11,0.6)]" />
          )}
        </div>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-wider font-bold text-stone-500">Verifactu · AEAT</p>
          <AnimatePresence mode="wait">
            <motion.p key={step >= 4 ? 'ok' : step >= 3 ? 'sign' : 'ready'} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
              className={`text-[11px] font-extrabold ${step >= 4 ? 'text-emerald-700' : step >= 3 ? 'text-amber-700' : 'text-stone-700'}`}>
              {step >= 4 ? 'Firmada · Hash SHA-256 encadenado' : step >= 3 ? 'Firmando…' : 'Listo para firmar'}
            </motion.p>
          </AnimatePresence>
        </div>
        {step >= 4 && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
            <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────
   ESCENA 3 — OCR escaneando un ticket
   ────────────────────────────────────────────────────────── */
const OCR_FIELDS = [
  { label: 'Proveedor', value: 'Repsol Estaciones SA', delay: 700 },
  { label: 'NIF',       value: 'A-78374114',          delay: 1100 },
  { label: 'Fecha',     value: '06/06/2026',          delay: 1500 },
  { label: 'Base',      value: '€37,44',              delay: 1900 },
  { label: 'IVA 21%',   value: '€7,86',               delay: 2300 },
  { label: 'Total',     value: '€45,30',              delay: 2700, big: true },
];

// Anchos estables de líneas del ticket (sintético, no aleatorio en render)
const TICKET_LINE_WIDTHS = [78, 65, 88, 70, 95, 62, 80, 73];

const SceneOcr = () => {
  const [visible, setVisible] = useState(0);
  useEffect(() => {
    const timers = OCR_FIELDS.map((_, i) =>
      setTimeout(() => setVisible((v) => Math.max(v, i + 1)), OCR_FIELDS[i].delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="p-5 grid grid-cols-2 gap-4">
      {/* Ticket mock with scan line */}
      <div className="relative bg-white border border-stone-200 rounded-lg overflow-hidden shadow-inner" style={{ minHeight: 220 }}>
        <div className="p-3 space-y-1.5">
          <p className="text-[10px] font-mono text-center font-bold text-stone-700">REPSOL ESTACIONES</p>
          <p className="text-[8px] font-mono text-center text-stone-400">A-78374114 · MADRID</p>
          <div className="border-t border-dashed border-stone-200 my-1" />
          {[
            ['DIESEL E+10 · 30,21L', '37,44'],
            ['IVA 21%', '7,86'],
            ['TOTAL EUR', '45,30'],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between text-[9px] font-mono text-stone-700">
              <span>{k}</span><span className="font-bold">{v}</span>
            </div>
          ))}
          <div className="border-t border-dashed border-stone-200 my-1" />
          <p className="text-[8px] font-mono text-center text-stone-400">GRACIAS POR SU COMPRA</p>
          <div className="space-y-0.5 mt-2">
            {TICKET_LINE_WIDTHS.map((w, i) => (
              <div key={i} className="h-0.5 bg-stone-100 rounded" style={{ width: `${w}%` }} />
            ))}
          </div>
        </div>
        {/* Scanning beam */}
        <motion.div
          initial={{ y: 0 }}
          animate={{ y: [0, 220, 0] }}
          transition={{ duration: 3.6, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-sky-400 to-transparent shadow-[0_0_16px_4px_rgba(56,189,248,0.6)]"
        />
        <div className="absolute top-2 right-2 bg-sky-500 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex items-center gap-1">
          <ScanLine className="w-2.5 h-2.5" /> OCR
        </div>
      </div>

      {/* Extracted fields */}
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-wider text-stone-400 font-bold flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-violet-500" /> Datos extraídos · Claude Vision
        </p>
        {OCR_FIELDS.map((f, i) => (
          <motion.div
            key={f.label}
            initial={{ opacity: 0, x: 10 }}
            animate={visible > i ? { opacity: 1, x: 0 } : { opacity: 0, x: 10 }}
            transition={{ duration: 0.35 }}
            className={`rounded-lg p-2 border ${f.big ? 'bg-gradient-to-br from-orange-500 to-rose-500 text-white border-transparent' : 'bg-stone-50 border-stone-100'}`}
          >
            <p className={`text-[9px] uppercase tracking-wider font-bold ${f.big ? 'text-white/80' : 'text-stone-400'}`}>{f.label}</p>
            <p className={`text-[11px] font-extrabold tabular-nums ${f.big ? 'text-white' : 'text-stone-800'}`}>{f.value}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────
   ESCENA 4 — Asesor IA fiscal
   ────────────────────────────────────────────────────────── */
const AI_USER = '¿Cuánto pagaré de IVA en Q2 y cuándo vence el modelo 303?';
const AI_BOT  = 'Según tu facturación actual: pagarás €2.620,18 de IVA. El modelo 303 del 2º trimestre vence el 20 de julio. Te recomiendo apartar €2.620 de tu tesorería ahora — puedes hacerlo en 1 click.';

const Typewriter = ({ text, speed = 18, onDone }) => {
  const [shown, setShown] = useState('');
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setShown(text.slice(0, i));
      if (i >= text.length) { clearInterval(id); onDone && onDone(); }
    }, speed);
    return () => clearInterval(id);
  }, [text, speed, onDone]);
  return <span>{shown}<span className="inline-block w-0.5 h-3 align-middle bg-stone-400 ml-0.5 animate-pulse" /></span>;
};

const SceneAi = () => {
  const [stage, setStage] = useState(0); // 0 user typed, 1 dots, 2 bot typing, 3 bot done
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 1200); // dots after user msg
    const t2 = setTimeout(() => setStage(2), 2400); // bot starts typing
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div className="p-5 flex flex-col gap-3" style={{ minHeight: 320 }}>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center"><Sparkles className="w-3.5 h-3.5 text-white" /></div>
        <div>
          <p className="text-xs font-extrabold text-stone-800">Asesor IA fiscal</p>
          <p className="text-[10px] text-stone-400">Claude · entrenado en tu contabilidad</p>
        </div>
        <span className="ml-auto text-[9px] bg-emerald-100 text-emerald-700 font-bold px-2 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> online
        </span>
      </div>

      {/* User bubble */}
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="ml-auto max-w-[80%]">
        <div className="bg-gradient-to-br from-orange-500 to-rose-500 text-white px-3 py-2 rounded-2xl rounded-tr-md text-[11px] leading-snug shadow-md">
          {AI_USER}
        </div>
        <div className="flex items-center gap-1 justify-end mt-1 text-[9px] text-stone-400">
          <User2 className="w-2.5 h-2.5" /> Tú · ahora
        </div>
      </motion.div>

      {/* Bot bubble */}
      <AnimatePresence>
        {stage >= 1 && (
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="max-w-[88%]">
            <div className="bg-stone-100 text-stone-800 px-3 py-2 rounded-2xl rounded-tl-md text-[11px] leading-snug shadow-sm">
              {stage === 1 && (
                <span className="inline-flex gap-1">
                  <motion.span animate={{ y: [0, -3, 0] }} transition={{ repeat: Infinity, duration: 0.8, delay: 0 }} className="w-1.5 h-1.5 rounded-full bg-stone-400" />
                  <motion.span animate={{ y: [0, -3, 0] }} transition={{ repeat: Infinity, duration: 0.8, delay: 0.15 }} className="w-1.5 h-1.5 rounded-full bg-stone-400" />
                  <motion.span animate={{ y: [0, -3, 0] }} transition={{ repeat: Infinity, duration: 0.8, delay: 0.3 }} className="w-1.5 h-1.5 rounded-full bg-stone-400" />
                </span>
              )}
              {stage >= 2 && <Typewriter text={AI_BOT} speed={14} onDone={() => setStage(3)} />}
            </div>
            <div className="flex items-center gap-1 mt-1 text-[9px] text-stone-400">
              <Sparkles className="w-2.5 h-2.5 text-violet-500" /> Asesor IA · pensando con tus datos reales
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input mock */}
      <div className="mt-auto flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-full pl-3 pr-1 py-1">
        <MessageSquare className="w-3.5 h-3.5 text-stone-400" />
        <p className="text-[10px] text-stone-400 flex-1">Pregunta cualquier cosa sobre tu fiscalidad…</p>
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center">
          <Send className="w-3 h-3 text-white" />
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────
   COMPONENT PRINCIPAL — loop de escenas
   ────────────────────────────────────────────────────────── */

const NOTIFICATIONS = [
  { icon: Check, text: 'FAC-00118 cobrada', color: 'emerald' },
  { icon: Camera, text: 'Ticket Repsol procesado', color: 'sky' },
  { icon: Bot, text: 'Asistente IA listo', color: 'violet' },
  { icon: ShieldCheck, text: 'Verifactu firmado', color: 'amber' },
  { icon: Building2, text: 'Modelo 303 calculado', color: 'orange' },
];

const HeroPreview = () => {
  const [scene, setScene] = useState(0);
  const [notif, setNotif] = useState(null);

  useEffect(() => {
    const id = setInterval(() => setScene((s) => (s + 1) % SCENES.length), 5500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      const n = NOTIFICATIONS[Math.floor(Math.random() * NOTIFICATIONS.length)];
      setNotif({ ...n, key: Date.now() });
    }, 4500);
    return () => clearInterval(id);
  }, []);

  const current = SCENES[scene];

  return (
    <div className="relative" data-testid="hero-preview" style={{ perspective: '1200px' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{ transformStyle: 'preserve-3d', minHeight: 440 }}
        className="relative bg-white rounded-3xl shadow-2xl shadow-stone-900/20 border border-stone-100 overflow-hidden"
      >
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 px-4 py-2.5 bg-stone-50 border-b border-stone-100">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
          <AnimatePresence mode="wait">
            <motion.div
              key={current.url}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.3 }}
              className="ml-3 px-3 py-1 bg-white rounded-lg text-[10px] font-mono text-stone-500 border border-stone-100 flex items-center gap-1.5 max-w-[260px] truncate"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {current.url}
            </motion.div>
          </AnimatePresence>

          {/* Scene indicator dots */}
          <div className="ml-auto flex items-center gap-1.5">
            {SCENES.map((_, i) => (
              <button
                key={i}
                onClick={() => setScene(i)}
                aria-label={`Ver escena ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === scene ? 'w-5 bg-orange-500' : 'w-1.5 bg-stone-300 hover:bg-stone-400'}`}
                data-testid={`hero-scene-dot-${i}`}
              />
            ))}
          </div>
        </div>

        {/* Scene container */}
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            {current.id === 'dashboard' && <SceneDashboard />}
            {current.id === 'invoice'   && <SceneInvoice />}
            {current.id === 'ocr'       && <SceneOcr />}
            {current.id === 'ai'        && <SceneAi />}
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Floating notification */}
      <AnimatePresence>
        {notif && (
          <motion.div
            key={notif.key}
            initial={{ opacity: 0, y: -10, x: 10 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, y: -10, x: 10 }}
            transition={{ duration: 0.4 }}
            className="absolute top-14 right-4 z-20 bg-white rounded-xl shadow-xl shadow-stone-900/15 border border-stone-100 px-3 py-2 flex items-center gap-2 max-w-[220px]"
          >
            <div className={`w-6 h-6 rounded-lg bg-${notif.color}-100 flex items-center justify-center shrink-0`}>
              <notif.icon className={`w-3 h-3 text-${notif.color}-600`} />
            </div>
            <p className="text-[10px] font-bold text-stone-700 truncate">{notif.text}</p>
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"
              animate={{ scale: [1, 1.6, 1] }}
              transition={{ duration: 1.2, repeat: Infinity }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scene-name floating badge bottom-left */}
      <motion.div
        key={`label-${scene}`}
        initial={{ opacity: 0, y: 8, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="absolute -bottom-3 left-6 z-20 bg-stone-900 text-white text-[10px] font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5"
        data-testid="hero-scene-label"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B4A] animate-pulse" />
        {current.label}
      </motion.div>

      {/* Bell icon */}
      <motion.div
        className="absolute -top-3 right-12 hidden md:flex w-8 h-8 rounded-full bg-white border border-stone-200 shadow-lg items-center justify-center"
        animate={{ rotate: [0, -15, 12, -8, 0] }}
        transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 5 }}
      >
        <Bell className="w-3.5 h-3.5 text-orange-500" />
        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-white" style={{ animation: 'pulse-soft 1.5s ease-in-out infinite' }} />
      </motion.div>
    </div>
  );
};

export default HeroPreview;
